import { Button, DatePicker, Form, Input, InputNumber, TimePicker, Select, message } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { extractTextContent, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';
import {
	getFileLink,
	getProtectedImageUriFromString,
	getVideoUriFromString,
} from '../../utils/FileAccessUtils';
import ReactPlayer from 'react-player';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import { useForm } from 'antd/lib/form/Form';
import {
	formatForTroubleshootingAnswerInput,
	getSubstepsIfAny,
	getTroubleshootingBackendAnswers,
	isAttachmentInputType,
	STEP_INPUT_TYPE_MAP,
	updateTroubleshootingAnswer,
} from '../../utils/TroubleshootingUtils';
import { DATE_FORMAT, DATE_ONLY_FORMAT, TIME_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import OWRadio from '../static_visual_radio_input/StaticVisualRadioInput';
import OWUpload from '../OWUpload';
import { imageUploadValidation } from '../../utils/ImageUtils';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import OWMultiSelect from '../static_visual_radio_input/OWMultiSelect';

const getInputFieldName = (step) => `troubleshooting-input-${nullSafeGet('orderingId', step)}`;

const TroubleshootingDisplay: FC<any> = ({
	troubleshootingSteps,
	onBack,
	onNextStep,
	onCancel,
	userType,
	existingAnswer,
	noStyle,
}): React.ReactElement => {
	const [stepIndex, setStepIndex] = useState(0);
	const [tsAnswers, setTsAnswers] = useState(existingAnswer || []);
	const [subSteps, setSubSteps] = useState([]);
	const [subStepAnswers, setSubStepAnswers] = useState([]);

	const currentStep: any = useMemo(
		() => nullSafeGetOrElse(`${stepIndex}`, troubleshootingSteps, {}),
		[stepIndex, troubleshootingSteps]
	);

	const initialiseSubStepsAndAnswer = useCallback((step, subSteps, answers) => {
		const existingAnswerForNewStep = answers.find(
			(_) => nullSafeGet('troubleshootingStep.orderingId', _) === step.orderingId
		);
		setSubStepAnswers(nullSafeGetOrElse('subStepAnswers', existingAnswerForNewStep, []));
		setSubSteps(subSteps);
	}, []);

	const initialiseStepNavingation = useCallback(
		(toBeStepIndex) => {
			const toBeStep = nullSafeGetOrElse(`${toBeStepIndex}`, troubleshootingSteps, {});
			const tobeAnswer = tsAnswers.find(
				(_) => nullSafeGet('troubleshootingStep.orderingId', _) === toBeStep.orderingId
			);
			const possibleSubSteps = getSubstepsIfAny(
				toBeStep,
				nullSafeGet('answer', tobeAnswer) || nullSafeGet('multipleAnswers', tobeAnswer)
			);
			setStepIndex(toBeStepIndex);
			nullSafeGet('0', possibleSubSteps) &&
				initialiseSubStepsAndAnswer(toBeStep, possibleSubSteps, tsAnswers);
		},
		[initialiseSubStepsAndAnswer, troubleshootingSteps, tsAnswers]
	);

	useEffect(() => {
		if (nullSafeGet('0', existingAnswer)) {
			const toBeStepIndex = troubleshootingSteps.length - 1;
			initialiseStepNavingation(toBeStepIndex);
		}
	}, [existingAnswer]);

	const [uploading, setUploading] = useState(false);

	const [form] = useForm();

	const { setFieldsValue } = form;

	const initialValues = useMemo(() => {
		const answers = existingAnswer || [];

		return answers.reduce(
			(acc, ans) => ({
				...acc,
				[getInputFieldName(nullSafeGetOrElse('troubleshootingStep', ans, {}))]:
					formatForTroubleshootingAnswerInput(ans),
			}),
			{}
		);
	}, [existingAnswer]);

	const { getFieldValue } = form;

	const inputFieldName = useMemo(() => getInputFieldName(currentStep), [currentStep]);

	// repeatable group inject first item
	useEffect(() => {
		if (nullSafeGet('inputType', currentStep) === STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP) {
			const existing =
				getFieldValue(getInputFieldName(currentStep)) || getFieldValue(inputFieldName);
			if (!existing || (Array.isArray(existing) && existing.length === 0)) {
				setFieldsValue({ [inputFieldName]: [{}] });
			}
		}
	}, [currentStep, getFieldValue, inputFieldName, setFieldsValue]);

	const defaultFileList = useMemo(() => {
		const answers = existingAnswer || [];
		const currentAnswer = answers.find(
			(_) => getInputFieldName(_.troubleshootingStep) === getInputFieldName(currentStep)
		);
		return isAttachmentInputType(currentStep)
			? currentAnswer
				? formatForTroubleshootingAnswerInput(currentAnswer)
				: []
			: [];
	}, [currentStep, existingAnswer]);

	const getFileLinkFromString = useCallback(
		(fileIdWithName) => {
			const fileId = fileIdWithName.split('/')[0];
			const fileName = fileIdWithName.split('/')[1];
			return getFileLink(fileId, fileName, userType);
		},
		[userType]
	);

	const getPdfElement = useCallback(
		(fileIdWithName) => <OWPdfViewer file={getFileLinkFromString(fileIdWithName)} />,
		[getFileLinkFromString]
	);

	const getImageElement = useCallback(
		(fileString) => {
			const link = getProtectedImageUriFromString(userType)(fileString);
			// eslint-disable-next-line jsx-a11y/img-redundant-alt
			return <img src={link} alt="Unable to display image" />;
		},
		[userType]
	);

	const getEmbedElement = useCallback(
		(html) => <div dangerouslySetInnerHTML={{ __html: html }} />,
		[]
	);

	const getVideoElement = useCallback((fileIdWithName) => {
		const link = getVideoUriFromString(fileIdWithName);
		return <ReactPlayer url={link} controls playing />;
	}, []);

	const attachmentElement = useMemo(() => {
		const attachments = nullSafeGetOrElse('attachments', currentStep, []);
		if (attachments.length > 0) {
			const fileIdWithName = nullSafeGetOrElse('fileIdWithName', attachments[0], '/');

			switch (attachments[0].attachmentType) {
				case 'image':
					return getImageElement(fileIdWithName);
				case 'pdf':
					return getPdfElement(fileIdWithName);
				case 'embeddedVideo':
					return getEmbedElement(fileIdWithName);
				case 'video':
					return getVideoElement(fileIdWithName);
			}
		}
		return null;
	}, [currentStep, getImageElement, getPdfElement, getEmbedElement, getVideoElement]);

	const onBackClick = useCallback(() => {
		stepIndex > 0 ? initialiseStepNavingation(stepIndex - 1) : onBack();
	}, [initialiseStepNavingation, onBack, stepIndex]);
	const showBackButton = useMemo(() => stepIndex > 0 || onBack, [stepIndex, onBack]);

	const proceedToNextStep = useCallback(
		(answers, subSteps?) => {
			nullSafeGet('0', subSteps)
				? initialiseSubStepsAndAnswer(currentStep, subSteps, answers)
				: stepIndex < troubleshootingSteps.length - 1
				? setStepIndex((i) => i + 1)
				: onNextStep(
						answers.map((_) => ({
							..._,
							...getTroubleshootingBackendAnswers(_),
						}))
				  );
		},
		[currentStep, initialiseSubStepsAndAnswer, onNextStep, stepIndex, troubleshootingSteps.length]
	);

	const inputErrorMessage = useMemo(() => {
		switch (currentStep.inputType) {
			case STEP_INPUT_TYPE_MAP.DATE_TIME:
				return 'Select a valid date and time';
			case STEP_INPUT_TYPE_MAP.DATE_ONLY:
				return 'Select a valid date';
			case STEP_INPUT_TYPE_MAP.TIME_RANGE:
			case STEP_INPUT_TYPE_MAP.TIME_ONLY:
				return 'Select a valid time';
			case STEP_INPUT_TYPE_MAP.NUMBER:
				return 'Enter a valid number';
			case STEP_INPUT_TYPE_MAP.TEXT:
				return 'Enter a valid text';
			case STEP_INPUT_TYPE_MAP.IMAGE:
				return 'You have to choose at least one image';
			case STEP_INPUT_TYPE_MAP.PDF:
				return 'You have to choose at least one pdf';
			case STEP_INPUT_TYPE_MAP.ANY_FILE:
				return 'You have to choose at least one file';
			case STEP_INPUT_TYPE_MAP.GROUP:
				return 'Enter valid values for the group';
			case STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP:
				return 'Add at least one group with valid values';
			case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE:
			default:
				return 'Select a valid choice';
		}
	}, [currentStep.inputType]);

	const onNextClick = useCallback(async () => {
		if (nullSafeGet('inputRequired', currentStep)) {
			// Validate group and repeatable group fields
			if (
				currentStep.inputType === STEP_INPUT_TYPE_MAP.GROUP ||
				currentStep.inputType === STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP
			) {
				try {
					await form.validateFields();
				} catch (e) {
					message.error(inputErrorMessage);
					return;
				}
			}
			const userCanSkipInput = nullSafeGet('userCanSkipInput', currentStep);
			const inputValue = getFieldValue(inputFieldName);
			if (
				!userCanSkipInput &&
				((Array.isArray(inputValue) && inputValue.length < 1) || !inputValue)
			) {
				message.error(inputErrorMessage);
			} else {
				const existingAnswer = tsAnswers.find(
					(_) => nullSafeGet('troubleshootingStep.orderingId', _) === currentStep.orderingId
				);
				const resetSubStepAnswers =
					currentStep.inputType === STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE &&
					inputValue !== nullSafeGet('answer', existingAnswer);
				const newAnswers = !!existingAnswer
					? tsAnswers.map((_) =>
							nullSafeGet('troubleshootingStep.orderingId', _) === currentStep.orderingId
								? {
										..._,
										...(resetSubStepAnswers && { subStepAnswers: [] }),
										...updateTroubleshootingAnswer(_.troubleshootingStep, inputValue),
								  }
								: _
					  )
					: [
							...tsAnswers,
							...[
								{
									troubleshootingStep: { ...currentStep },
									...updateTroubleshootingAnswer(currentStep, inputValue),
								},
							],
					  ];
				setTsAnswers(newAnswers);
				proceedToNextStep(newAnswers, getSubstepsIfAny(currentStep, inputValue));
			}
		} else {
			proceedToNextStep(tsAnswers);
		}
	}, [currentStep, getFieldValue, inputErrorMessage, inputFieldName, proceedToNextStep, tsAnswers]);

	const handleImageUpdate = useCallback(
		(info) => {
			setFieldsValue({ [inputFieldName]: info.fileList.filter((img) => img.status === 'done') });
		},
		[inputFieldName, setFieldsValue]
	);

	const onImageUploadError = useCallback(() => {
		message.error('Image upload failed! Please contact support!');
		setUploading(false);
	}, []);

	const onMultiChoiceChange = useCallback(
		(step) => (ans) => {
			setFieldsValue({ [getInputFieldName(step)]: ans });
			setTimeout(() => {
				onNextClick();
			}, 350);
		},
		[onNextClick, setFieldsValue]
	);

	const onMultipleAnswerMultiChoiceChange = useCallback(
		(step) => (ans) => {
			setFieldsValue({ [getInputFieldName(step)]: ans });
		},
		[setFieldsValue]
	);

	const renderInputField = useCallback(
		(field, isGroupField = false) => {
			const inputType = field.inputType || field;
			const options = nullSafeGetOrElse('options', field, []);

			const baseStyle = isGroupField ? { width: '100%' } : {};
			const size = isGroupField ? undefined : 'large';

			switch (inputType) {
				case STEP_INPUT_TYPE_MAP.TEXT:
					return <Input.TextArea autoSize style={{ width: '100%' }} size={size} />;
				case STEP_INPUT_TYPE_MAP.NUMBER:
					return <InputNumber style={baseStyle} size={size} />;
				case STEP_INPUT_TYPE_MAP.DATE_ONLY:
					return <DatePicker format={DATE_ONLY_FORMAT} style={baseStyle} size={size} />;
				case STEP_INPUT_TYPE_MAP.DATE_TIME:
					return <DatePicker format={DATE_FORMAT} showTime size={size} />;
				case STEP_INPUT_TYPE_MAP.TIME_ONLY:
					return <TimePicker use12Hours format={TIME_ONLY_FORMAT} minuteStep={15} size={size} />;
				case STEP_INPUT_TYPE_MAP.TIME_RANGE:
					return <TimePicker.RangePicker size={size} format={TIME_ONLY_FORMAT} />;
				case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE: {
					if (isGroupField) {
						return (
							<Select style={{ width: '100%', textAlign: 'left' }}>
								{options.map((opt, idx) => (
									<Select.Option key={`opt_${idx}`} value={opt}>
										{opt}
									</Select.Option>
								))}
							</Select>
						);
					} else {
						const MultipleChoiceComponent = field.allowMultipleAnswerSelections
							? OWMultiSelect
							: OWRadio;
						const onChangeMethod = field.allowMultipleAnswerSelections
							? onMultipleAnswerMultiChoiceChange(field)
							: onMultiChoiceChange(field);
						const multipleChoices = nullSafeGetOrElse('multipleChoices', field, []);
						return (
							<MultipleChoiceComponent
								renderRecord={(location) => location.name}
								records={[...multipleChoices].reduce(
									(acc, choice, orderidngId) => ({
										...acc,
										[choice.value]: {
											id: choice.value,
											name: `${choice.value}${
												choice.description ? ` (${choice.description})` : ''
											}`,
											orderidngId,
										},
									}),
									{}
								)}
								fieldName={inputFieldName}
								sorter={(records) => records.sort((a, b) => a.orderingId - b.orderingId)}
								onChange={onChangeMethod}
								onClick={onChangeMethod}
							/>
						);
					}
				}
				case STEP_INPUT_TYPE_MAP.IMAGE:
					return (
						<OWUpload
							accept="image/*"
							name="file"
							listType="picture-card"
							className="workorder-ts-image-uploader"
							multiple={true}
							beforeUpload={imageUploadValidation}
							onChange={handleImageUpdate}
							onError={onImageUploadError}
							defaultFileList={defaultFileList}
						>
							<div style={{ display: 'inline' }}>
								<UploadOutlined type={uploading ? 'loading' : 'plus'} />
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					);
				case STEP_INPUT_TYPE_MAP.PDF:
				case STEP_INPUT_TYPE_MAP.ANY_FILE:
					return (
						<OWUpload
							accept={inputType === STEP_INPUT_TYPE_MAP.PDF ? '.pdf' : undefined}
							name="file"
							listType="picture-card"
							className="workorder-ts-image-uploader"
							multiple={true}
							onChange={handleImageUpdate}
							onError={onImageUploadError}
							defaultFileList={defaultFileList}
						>
							<div style={{ display: 'inline' }}>
								<UploadOutlined type={uploading ? 'loading' : 'plus'} />
								<div className="ant-upload-text">Upload</div>
							</div>
						</OWUpload>
					);
				default:
					return isGroupField ? <Input /> : null;
			}
		},
		[
			defaultFileList,
			handleImageUpdate,
			onImageUploadError,
			onMultiChoiceChange,
			onMultipleAnswerMultiChoiceChange,
			inputFieldName,
			uploading,
		]
	);

	const getInputField = useCallback(
		(step) => {
			const { inputType } = step;

			switch (inputType) {
				case STEP_INPUT_TYPE_MAP.GROUP: {
					const fields = nullSafeGetOrElse('groupFields', step, []);
					return (
						<div style={{ width: '100%', textAlign: 'left' }}>
							<div style={{ display: 'flex', flexWrap: 'wrap' }}>
								{fields.map((field, idx) => {
									return (
										<Form.Item
											key={`grp_${idx}`}
											name={[inputFieldName, field.name || `field_${idx}`]}
											label={field.label}
											style={{ width: '50%', paddingRight: 8 }}
											required={field.required}
											rules={[{ required: field.required, message: 'Required' }]}
										>
											{renderInputField(field, true)}
										</Form.Item>
									);
								})}
							</div>
						</div>
					);
				}
				case STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP: {
					const fields = nullSafeGetOrElse('groupFields', step, []);
					return (
						<Form.List name={inputFieldName}>
							{(items, { add, remove }) => (
								<div style={{ width: '100%' }}>
									{items.map((item) => (
										<div key={item.key} className="mb-3 border border-gray-200 p-3">
											<div className="mb-2 flex justify-between">
												<div className="bodybold mr-2"> Group {item.key + 1}</div>
												<Button
													icon={<DeleteOutlined />}
													type="link"
													onClick={() => remove(item.name)}
												>
													Remove
												</Button>
											</div>
											<div style={{ display: 'flex', flexWrap: 'wrap' }}>
												{fields.map((field, idx) => (
													<Form.Item
														key={`rgrp_${item.key}_${idx}`}
														name={[item.name, field.name || `field_${idx}`]}
														label={field.label}
														style={{ width: '50%', paddingRight: 8 }}
														required={field.required}
														rules={[{ required: field.required, message: 'Required' }]}
													>
														{renderInputField(field, true)}
													</Form.Item>
												))}
											</div>
										</div>
									))}
									<Button type="dashed" onClick={() => add()} style={{ width: '100%' }}>
										<PlusOutlined /> Add
									</Button>
								</div>
							)}
						</Form.List>
					);
				}
				case STEP_INPUT_TYPE_MAP.DATE_ONLY:
				case STEP_INPUT_TYPE_MAP.DATE_TIME:
				case STEP_INPUT_TYPE_MAP.TIME_ONLY:
				case STEP_INPUT_TYPE_MAP.TIME_RANGE:
				case STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE:
				case STEP_INPUT_TYPE_MAP.NUMBER:
				case STEP_INPUT_TYPE_MAP.TEXT:
				case STEP_INPUT_TYPE_MAP.IMAGE:
				case STEP_INPUT_TYPE_MAP.PDF:
				case STEP_INPUT_TYPE_MAP.ANY_FILE:
					return renderInputField(step, false);
				default:
					return null;
			}
		},
		[renderInputField, inputFieldName]
	);

	const inputField = useMemo(
		() => (!!currentStep.inputType ? getInputField(currentStep) : null),
		[currentStep, getInputField]
	);

	const onSubStepBack = () => setSubSteps([]);

	const onSubStepNext = useCallback(
		(subStepAnswers) => {
			const existingAnswer = tsAnswers.find(
				(_) => nullSafeGet('troubleshootingStep.orderingId', _) === currentStep.orderingId
			);
			const newAnswers = !!existingAnswer
				? tsAnswers.map((_) =>
						nullSafeGet('troubleshootingStep.orderingId', _) === currentStep.orderingId
							? {
									..._,
									subStepAnswers,
							  }
							: _
				  )
				: [
						...tsAnswers,
						...[
							{
								troubleshootingStep: { ...currentStep },
								subStepAnswers,
							},
						],
				  ];
			setTsAnswers(newAnswers);
			setSubSteps([]);
			setSubStepAnswers([]);
			proceedToNextStep(newAnswers);
		},
		[currentStep, proceedToNextStep, tsAnswers]
	);

	return nullSafeGet('0', subSteps) ? (
		<TroubleshootingDisplay
			troubleshootingSteps={subSteps}
			onBack={onSubStepBack}
			onNextStep={onSubStepNext}
			onCancel={onCancel}
			existingAnswer={subStepAnswers}
			noStyle={noStyle}
		/>
	) : (
		<div
			key={5}
			style={{
				...(noStyle
					? {}
					: {
							marginTop: '15vh',
							width: '750px',
							marginBottom: '80px',
					  }),
				...{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					maxWidth: '100vw',
					padding: 16,
				},
			}}
		>
			<h3 className="troubleshooting-title">
				{currentStep.inputRequired ? (
					extractTextContent(currentStep.title)
				) : (
					<span dangerouslySetInnerHTML={{ __html: currentStep.title }} />
				)}
				{currentStep.userCanSkipInput ? (
					<div className="mb-4 text-center">
						<Button onClick={onNextClick} type="link">
							Skip
						</Button>
					</div>
				) : null}
				<div className="text-xl font-normal">
					<span dangerouslySetInnerHTML={{ __html: currentStep.description }} />
				</div>
			</h3>
			<Form
				form={form}
				layout="vertical"
				requiredMark={true}
				initialValues={initialValues}
				style={{
					width: '750px',
					maxWidth: '100vw',
					padding: 16,
					alignItems: 'center',
					textAlign: 'center',
				}}
			>
				{inputField ? (
					currentStep.inputType === STEP_INPUT_TYPE_MAP.GROUP ||
					currentStep.inputType === STEP_INPUT_TYPE_MAP.REPEATABLE_GROUP ? (
						<div style={{ width: '100%' }}>{inputField}</div>
					) : (
						<Form.Item name={inputFieldName} style={{ width: '100%' }}>
							{inputField}
						</Form.Item>
					)
				) : null}
				{attachmentElement ? (
					<Form.Item>
						<div className="flex flex-col items-center">
							<div>{attachmentElement}</div>
						</div>
					</Form.Item>
				) : null}
			</Form>
			<div
				className={`flex flex-row ${
					currentStep.inputType !== STEP_INPUT_TYPE_MAP.MULTIPLE_CHOICE ? 'mt-8' : ''
				} `}
			>
				{showBackButton ? (
					<Button size="large" type="ghost" onClick={onBackClick}>
						Back
					</Button>
				) : null}
				{nullSafeGetOrElse('canProceedToNextStep', currentStep, false) ? (
					<Button size="large" type="primary" className="ml-4" onClick={onNextClick}>
						Next step
					</Button>
				) : (
					<Button size="large" type="primary" className="ml-4" onClick={onCancel}>
						Okay
					</Button>
				)}
			</div>
		</div>
	);
};

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(TroubleshootingDisplay as any);
